import { memo, useMemo, useState, type ReactNode } from 'react';
import {
  ClockCounterClockwiseIcon,
  FunctionIcon,
  FunnelXIcon,
  WarningCircleIcon,
  SortAscendingIcon,
  SortDescendingIcon,
} from '@phosphor-icons/react';
import { Num, T, useGT } from 'gt-react';
import type {
  WorkbookCell,
  WorkbookColumnarSheet,
  WorkbookResult,
  WorkbookTable,
} from '@erp/shared-types';
import { sortPreviewRows, type SheetPlan } from '@erp/workbooks';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  hasColumnFormula,
  inputColumnIsField,
  worksheetColumnLetter,
} from './worksheetColumns';
import { WorksheetColumnMenu } from './WorksheetColumnMenu';
import { fieldDistinctValues } from './sampleDistinctValues';
import { useFilterDescription } from './WorksheetFilters';
import { WorksheetRowSummary } from './WorksheetRowSummary';
import { filterRemovals } from './workbookRunResults';
import type { ChangeSheet } from './useWorkbookEditor';
import {
  NeedsRunCell,
  WorkbookCellValue,
  workbookCellFont,
} from './WorkbookCellValue';
import { resultFilterTarget, useCellMenu } from './WorkbookCellMenu';
import { WorksheetSortBadge } from './WorksheetSortBadge';
import { useSortDirectionLabel } from './worksheetTypeLabels';
import {
  outputGridRows,
  type AppliedRowSteps,
  type GridFocus,
} from './worksheetRowView';

import {
  addPendingFilter,
  shownPendingFilters,
  willBeFilteredOut,
  type PendingFilters,
} from './worksheetPendingFilters';
import type { NotePendingFilter } from './worksheetValueList';
import {
  columnValidationStatus,
  validationStates,
} from './worksheetValidations';
import { ColumnValidationMark } from './WorksheetValidationMark';
import { Checkbox } from '@/components/ui/checkbox';

export function WorksheetInputGrid({
  table,
  references,
  sheet,
  result,
  change,
  selected,
  toggle,
  readOnly,
}: {
  table: WorkbookTable;
  references: string[];
  sheet: WorkbookColumnarSheet;
  /** The Worksheet's shown Result, whose Distinct values include its input Fields. */
  result?: WorkbookResult;
  change: ChangeSheet;
  selected: string[];
  toggle: (name: string) => void;
  readOnly: boolean;
}) {
  const gt = useGT();
  const { cellProps, menu } = useCellMenu(
    readOnly
      ? undefined
      : {
          sheet,
          change,
          filters: (column) => inputColumnIsField(sheet, column),
        },
  );
  const highlights = useColumnHighlights(table.columns, references, selected);
  return (
    <div className="max-h-52 overflow-auto border">
      <table
        className="w-full border-separate border-spacing-0 text-xs"
        aria-label={gt('Source data', { context: 'worksheet input grid' })}
      >
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className="sticky left-0 z-20 w-10 border-b border-r bg-muted px-2 py-1 text-muted-foreground">
              #
            </th>
            {table.columns.map((name) => (
              <th
                key={name}
                className={`min-w-36 border-b border-r text-left font-medium ${references.includes(name) ? 'bg-primary/10 text-foreground' : ''}`}
                title={
                  references.includes(name)
                    ? gt('Referenced in the selected formula', {
                        context: 'worksheet formula reference highlight',
                      })
                    : undefined
                }
              >
                <div className="flex items-center">
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-2 py-1 hover:bg-accent">
                    <Checkbox
                      disabled={readOnly}
                      checked={selected.includes(name)}
                      onCheckedChange={() => toggle(name)}
                    />
                    {name}
                  </label>
                  <WorksheetColumnMenu
                    input
                    sheet={sheet}
                    column={name}
                    readOnly={readOnly}
                    change={change}
                    // A Run reads only the columns the Workbook uses; the
                    // rest show a sample of the preview's first rows.
                    distinct={fieldDistinctValues(
                      result?.distinctValues,
                      name,
                      table,
                    )}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Input rows in input order: the sort applies to Fields after formulas. */}
          {table.rows.slice(0, 100).map((row, index) => (
            <InputGridRow
              key={index}
              index={index}
              cells={row}
              columns={table.columns}
              highlights={highlights}
              cellProps={cellProps}
            />
          ))}
        </tbody>
      </table>
      {menu}
      {!table.rows.length && (
        <p className="p-3 text-xs text-muted-foreground">
          <T context="worksheet empty input grid">
            This data has no rows. You can still select its columns.
          </T>
        </p>
      )}
    </div>
  );
}

// fallow-ignore-next-line complexity
export function WorksheetOutputGrid({
  sheet,
  sheetPlan,
  result,
  stale,
  needsRunHint,
  invalidColumns,
  selected,
  select,
  readOnly,
  change,
  focus,
  summaryAside,
  toolbar,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  result?: WorkbookResult;
  stale: boolean;
  /** The tooltip on columns the shown Result has no values for, naming the Run that fills them. */
  needsRunHint: string;
  /** Indexes of output columns with a Diagnostic. */
  invalidColumns: ReadonlySet<number>;
  selected: number;
  select: (index: number) => void;
  readOnly: boolean;
  change: ChangeSheet;
  focus?: GridFocus;
  /** Shown on the row summary's line, after it, such as how the shown rows were sampled. */
  summaryAside?: ReactNode;
  /** Shown joined to the grid's top edge, such as the selected column's formula bar. */
  toolbar?: ReactNode;
}) {
  const gt = useGT();
  const describeFilter = useFilterDescription();
  const validation = validationStates(sheet, result);
  const sortLabel = useSortDirectionLabel();
  const [pending, setPending] = useState<PendingFilters | null>(null);
  // Hint at the rows a filter change removes until a Result computed with it shows.
  const notePending: NotePendingFilter = (filter, after) => {
    if (result)
      setPending((current) =>
        addPendingFilter(current, filter, result, sheet.filters, after),
      );
  };
  const shownPending = shownPendingFilters(pending, sheet, result, stale);
  const { cellProps, menu } = useCellMenu(
    resultFilterTarget(sheet, readOnly ? undefined : change, notePending),
  );
  const [toggles, setToggles] = useState<AppliedRowSteps>({
    filters: true,
    sort: true,
  });
  const steps = {
    filters: sheet.filters.length > 0,
    sort: sheet.sort.length > 0,
  };
  // A step the Worksheet does not have cannot be turned off.
  const applied = {
    filters: toggles.filters || !steps.filters,
    sort: toggles.sort || !steps.sort,
  };
  const {
    rows,
    rowNumbers,
    inputOrder,
    exact: previewExact,
  } = outputGridRows(
    result,
    applied,
    (shown) =>
      sheetPlan
        ? sortPreviewRows(shown, sheetPlan, sheet.sort)
        : { rows: shown.rows, exact: true },
    focus,
  );
  const rowNumber = (index: number) => (rowNumbers?.[index] ?? index) + 1;
  const removals = filterRemovals(result, sheet.filters);
  const removedBy = (index: number) => {
    const filter = result?.filterCounts?.filters[index]?.filter;
    return filter
      ? gt('Removed by filter: {filter}', {
          context: 'worksheet row removed by filter',
          filter: describeFilter(filter),
        })
      : gt('Removed by a filter', {
          context: 'worksheet row removed by filter',
        });
  };
  const willBeFilteredOutLabel = gt('Will be filtered out', {
    context: 'worksheet row a pending filter change removes',
  });
  const layout = useColumnLayout(sheet.columns, result?.columns);
  return (
    <div className="flex flex-1 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {result ? (
          <WorksheetRowSummary
            result={result}
            sheet={sheet}
            applied={applied}
            setApplied={setToggles}
          />
        ) : (
          // Holds the summary's line, so the grid doesn't move when a Result arrives.
          <p className="text-xs text-muted-foreground">
            <T context="worksheet result row count before any run">
              Not run yet
            </T>
          </p>
        )}
        {summaryAside}
      </div>
      <div className="flex flex-1 flex-col">
        {toolbar}
        <div className="min-h-64 flex-1 overflow-auto border contain-size">
          <table
            className="w-full border-separate border-spacing-0 text-xs"
            aria-label={gt('Output data', { context: 'worksheet output grid' })}
          >
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                <th className="sticky left-0 z-20 w-10 border-b border-r bg-muted px-2 py-1 text-muted-foreground">
                  {sheet.sort.length ? (
                    <WorksheetSortBadge
                      sheet={sheet}
                      sheetPlan={sheetPlan}
                      readOnly={readOnly}
                      change={change}
                    />
                  ) : (
                    '#'
                  )}
                </th>
                {sheet.columns.map((column, index) => {
                  const sort = sheet.sort.find(
                    (item) => item.column === column.name,
                  );
                  return (
                    <th
                      key={index}
                      className="min-w-40 border-b border-r text-left font-medium"
                    >
                      <div className="flex items-center">
                        <button
                          type="button"
                          aria-pressed={selected === index}
                          onClick={() => select(index)}
                          className={`flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left outline-offset-[-2px] hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring ${selected === index ? 'bg-accent text-accent-foreground shadow-[inset_0_-2px_0_var(--color-primary)]' : ''}`}
                        >
                          <span className="text-muted-foreground tabular-nums">
                            {worksheetColumnLetter(index)}
                          </span>{' '}
                          <span className="flex-1">{column.name}</span>
                          {!result?.columns.includes(column.name) && (
                            <ClockCounterClockwiseIcon
                              size={14}
                              role="img"
                              className="text-muted-foreground"
                              aria-label={needsRunHint}
                            >
                              <title>{needsRunHint}</title>
                            </ClockCounterClockwiseIcon>
                          )}
                          {invalidColumns.has(index) && (
                            <WarningCircleIcon
                              size={14}
                              className="text-destructive"
                              aria-label={gt('Column has a problem', {
                                context:
                                  'worksheet column diagnostic indicator',
                              })}
                            />
                          )}
                          {hasColumnFormula(column) && (
                            <FunctionIcon
                              size={14}
                              aria-label={gt('Formula applied', {
                                context: 'worksheet column formula indicator',
                              })}
                            />
                          )}
                          {sort && (
                            <SortIndicator
                              direction={sort.direction}
                              label={gt('Sorted {order}', {
                                context: 'worksheet column sort indicator',
                                order: sortLabel(column.type, sort.direction),
                              })}
                            />
                          )}
                          <ColumnValidationMark
                            status={columnValidationStatus(
                              sheet,
                              validation,
                              column.name,
                            )}
                          />
                        </button>
                        <WorksheetColumnMenu
                          sheet={sheet}
                          sheetPlan={sheetPlan}
                          column={column.name}
                          readOnly={readOnly}
                          change={change}
                          removals={removals}
                          stale={stale}
                          distinct={result?.distinctValues?.find(
                            (field) => field.column === column.name,
                          )}
                          notePending={notePending}
                          validations={{
                            type: column.type,
                            states: validation,
                          }}
                        />
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const goes =
                  row.removedBy === null &&
                  willBeFilteredOut(
                    shownPending,
                    result?.columns ?? [],
                    row.cells,
                  );
                return (
                  <OutputGridRow
                    key={index}
                    index={index}
                    number={rowNumber(index)}
                    cells={row.cells}
                    removedLabel={
                      row.removedBy === null ? null : removedBy(row.removedBy)
                    }
                    goes={goes}
                    goesLabel={willBeFilteredOutLabel}
                    layout={layout}
                    selected={selected}
                    cellProps={cellProps}
                    needsRunHint={needsRunHint}
                  />
                );
              })}
              {Array.from(
                { length: Math.max(0, 18 - rows.length) },
                (_, index) => (
                  <tr key={`empty-${index}`} aria-hidden="true">
                    <td className="sticky left-0 z-[5] h-7 border-b border-r bg-muted px-2 text-right tabular-nums text-muted-foreground">
                      {rowNumbers ? '' : rows.length + index + 1}
                    </td>
                    {sheet.columns.map((_, col) => (
                      <td key={col} className="h-7 border-b border-r" />
                    ))}
                  </tr>
                ),
              )}
            </tbody>
          </table>
          {menu}
          {result && !result.rowCount && !rows.length && (
            <p className="p-3 text-xs text-muted-foreground">
              <T context="worksheet empty result grid">
                No rows matched the filters in the last run.
              </T>
            </p>
          )}
          {inputOrder?.bounded && (
            <p
              role="note"
              className="sticky bottom-0 border-t bg-muted/60 px-2 py-1 text-xs text-muted-foreground"
            >
              <T context="worksheet grid shows only the leading input rows">
                Showing the first <Num>{result?.inputOrderRows?.length}</Num>{' '}
                rows in their original order.
              </T>
            </p>
          )}
          {result && !inputOrder && stale && !previewExact && (
            <p
              role="status"
              className="sticky bottom-0 border-t bg-muted/60 px-2 py-1 text-xs text-muted-foreground"
            >
              <T context="worksheet preview order approximate">
                Preview order is approximate. Run the workbook for the exact
                order.
              </T>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Each input column's cell highlight — referenced by the selected Formula, or
 * selected — kept the same array while they are, so rows needn't re-render.
 */
function useColumnHighlights(
  columns: readonly string[],
  references: readonly string[],
  selected: readonly string[],
): readonly string[] {
  const key = JSON.stringify(
    columns.map((name) =>
      references.includes(name)
        ? 'bg-primary/5'
        : selected.includes(name)
          ? 'bg-accent/50'
          : '',
    ),
  );
  return useMemo((): string[] => JSON.parse(key), [key]);
}

/** One input row, memoized on the Source's own row and the highlights. */
const InputGridRow = memo(function InputGridRow({
  index,
  cells,
  columns,
  highlights,
  cellProps,
}: {
  index: number;
  cells: readonly WorkbookCell[];
  columns: readonly string[];
  highlights: readonly string[];
  cellProps: CellProps;
}) {
  return (
    <tr>
      <td className="sticky left-0 z-[5] border-b border-r bg-muted px-2 py-1 text-right tabular-nums text-muted-foreground">
        {index + 1}
      </td>
      {cells.map((cell, col) => (
        <td
          key={col}
          className={`max-w-64 truncate border-b border-r px-2 py-1 outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring ${highlights[col] ?? ''}`}
          {...cellProps(index, col, cell, columns[col] ?? '')}
        >
          <WorkbookCellValue cell={cell} />
        </td>
      ))}
    </tr>
  );
});

/** Where each output column's value sits in a Result row, and how it shows. */
type ColumnLayout = readonly {
  type: WorkbookColumnarSheet['columns'][number]['type'];
  /** The column's index in the Result, or -1 when the Result has no values for it. */
  at: number;
  name: string;
}[];

/**
 * The output columns' layout, kept the same object while their names, types
 * and places in the Result are: typing a Formula changes the Worksheet's
 * columns on every key but none of that, so the rows need not re-render.
 */
function useColumnLayout(
  columns: WorkbookColumnarSheet['columns'],
  resultColumns: readonly string[] | undefined,
): ColumnLayout {
  const key = JSON.stringify(
    columns.map((column) => [
      column.name,
      column.type,
      resultColumns?.indexOf(column.name) ?? -1,
    ]),
  );
  return useMemo(() => {
    const parsed: [string, ColumnLayout[number]['type'], number][] =
      JSON.parse(key);
    return parsed.map(([name, type, at]) => ({ name, type, at }));
  }, [key]);
}

type CellProps = ReturnType<typeof useCellMenu>['cellProps'];

/**
 * One output row. Memoized: its cells are the Result's own rows, so an edit
 * that leaves the Result and the column layout alone re-renders no row.
 */
const OutputGridRow = memo(function OutputGridRow({
  index,
  number,
  cells,
  removedLabel,
  goes,
  goesLabel,
  layout,
  selected,
  cellProps,
  needsRunHint,
}: {
  index: number;
  number: number;
  cells: readonly WorkbookCell[];
  /** The filter that removed the row, shown in input order; null when kept. */
  removedLabel: string | null;
  /** A pending filter change will remove it. */
  goes: boolean;
  goesLabel: string;
  layout: ColumnLayout;
  selected: number;
  cellProps: CellProps;
  needsRunHint: string;
}) {
  return (
    <tr
      className={
        removedLabel !== null
          ? 'bg-muted/30 text-muted-foreground'
          : goes
            ? 'bg-muted/30 text-muted-foreground line-through opacity-60'
            : undefined
      }
    >
      <td
        className="sticky left-0 z-[5] border-b border-r bg-muted px-2 py-1 text-right tabular-nums text-muted-foreground"
        title={goes ? goesLabel : undefined}
      >
        {removedLabel === null ? (
          <>
            {number}
            {goes && <span className="sr-only">{goesLabel}</span>}
          </>
        ) : (
          <RemovedRowMark number={number} label={removedLabel} />
        )}
      </td>
      {layout.map((column, col) => {
        const cell = column.at >= 0 ? cells[column.at] : undefined;
        return (
          <td
            key={col}
            className={`h-7 max-w-64 truncate border-b border-r px-2 py-1 outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring ${workbookCellFont(column.type)} ${selected === col ? 'bg-accent/30' : ''}`}
            {...(column.at >= 0 && cell !== undefined
              ? cellProps(index, col, cell, column.name, column.type)
              : {})}
          >
            {column.at < 0 ? (
              <NeedsRunCell hint={needsRunHint} />
            ) : cell === undefined ? (
              ''
            ) : (
              <WorkbookCellValue cell={cell} type={column.type} />
            )}
          </td>
        );
      })}
    </tr>
  );
});

function SortIndicator({
  direction,
  label,
}: {
  direction: 'asc' | 'desc';
  label: string;
}) {
  const Icon = direction === 'asc' ? SortAscendingIcon : SortDescendingIcon;
  return (
    <Icon size={14} role="img" aria-label={label}>
      <title>{label}</title>
    </Icon>
  );
}

/** A filtered-out row's number, with a tooltip naming the filter that removed it. */
function RemovedRowMark({ number, label }: { number: number; label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
        >
          <FunnelXIcon size={12} role="img" aria-label={label} />
          {number}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
